<script setup lang="ts">
import { computed } from 'vue';
import type { ProductBundle } from '@features/bundle';

const props = defineProps<{
  bundle: ProductBundle;
  currentStepIndex: number;
  selections: Record<number, number>;
  /** Картинка основного продукта (первая «таблетка» — всегда done) */
  baseProductImg: string | null;
  baseProductName: string;
}>();

const emit = defineEmits<{
  (e: 'go-to-step', index: number): void;
}>();

type StepStatus = 'done' | 'active' | 'pending';

const getStepStatus = (index: number): StepStatus => {
  if (index < props.currentStepIndex) return 'done';
  if (index === props.currentStepIndex) return 'active';
  return 'pending';
};

const getStepImage = (index: number): string | null => {
  const step = props.bundle.steps[index];
  if (!step) return null;
  const pid = props.selections[step.id];
  if (!pid) return null;
  const product = step.products.find((p) => p.product_id === pid);
  if (!product?.img) return null;
  if (typeof product.img === 'string') return product.img;
  if (Array.isArray(product.img)) return product.img[0]?.url ?? null;
  return null;
};

/** Базовый URL картинки основного продукта */
const baseImgUrl = computed<string | null>(() => {
  if (!props.baseProductImg) return null;
  return props.baseProductImg;
});
</script>

<template>
  <div class="step-indicator">
    <!-- Базовый продукт (бургер) — всегда первый, всегда done -->
    <div class="step-indicator__item step-indicator__item--done">
      <div class="step-indicator__thumb step-indicator__thumb--done">
        <img v-if="baseImgUrl" :src="baseImgUrl" :alt="baseProductName" />
        <UIcon v-else name="i-lucide-package" class="w-5 h-5 text-gray-400" />
      </div>
      <span class="step-indicator__label">{{ baseProductName }}</span>
    </div>

    <!-- Шаги бандла -->
    <template v-for="(step, index) in bundle.steps" :key="step.id">
      <!-- Соединительная линия -->
      <div
        class="step-indicator__line"
        :class="`step-indicator__line--${getStepStatus(index)}`"
      />

      <!-- Шаг -->
      <button
        type="button"
        class="step-indicator__item"
        :class="`step-indicator__item--${getStepStatus(index)}`"
        :disabled="index > currentStepIndex"
        @click="index <= currentStepIndex && emit('go-to-step', index)"
      >
        <div
          class="step-indicator__thumb"
          :class="`step-indicator__thumb--${getStepStatus(index)}`"
        >
          <img
            v-if="getStepImage(index)"
            :src="getStepImage(index)!"
            :alt="step.name"
          />
          <UIcon
            v-else
            name="i-lucide-circle-dashed"
            class="w-5 h-5 text-gray-400"
          />
        </div>
        <span class="step-indicator__label">{{ step.name }}</span>
      </button>
    </template>
  </div>
</template>

<style scoped lang="scss">
.step-indicator {
  @apply flex items-start gap-0 overflow-x-auto pb-1;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  &__item {
    @apply flex flex-col items-center gap-1 flex-shrink-0 min-w-[60px] cursor-pointer bg-transparent border-0 p-0;

    &--pending {
      @apply opacity-50 cursor-default;
    }
  }

  &__thumb {
    @apply w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center;
    @apply bg-gray-100 dark:bg-gray-700 border-2;

    img {
      @apply w-full h-full object-cover;
    }

    &--done {
      @apply border-green-500;
    }

    &--active {
      @apply border-orange-400;
    }

    &--pending {
      @apply border-gray-300 dark:border-gray-600;
    }
  }

  &__label {
    @apply text-[10px] text-center text-gray-600 dark:text-gray-400 leading-tight max-w-[56px];
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__line {
    @apply flex-shrink-0 self-center mt-0 h-0.5 w-4;

    &--done {
      @apply bg-green-400;
    }

    &--active {
      @apply bg-orange-300;
    }

    &--pending {
      @apply bg-gray-300 dark:bg-gray-600;
    }
  }
}
</style>
